import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type {
  Rarity,
  So5LeaderboardSeasonality,
} from '@sorare/core/src/__generated__/globalTypes';

import type {
  useHierarchiedLineups_so5LineupImport,
  useHierarchiedSo5Lineups_so5Lineup,
} from './__generated__/useHierarchiedLineups.graphql';

type SortedLineups<
  T extends
    | useHierarchiedLineups_so5LineupImport
    | useHierarchiedSo5Lineups_so5Lineup,
> = {
  so5LeaderboardGroup: NonNullable<T['so5Leaderboard']>['so5LeaderboardGroup'];
  competitions: {
    mainRarityType: Rarity | null;
    seasonality: So5LeaderboardSeasonality | null;
    lineups: T[];
  }[];
}[];

export const useHierarchiedLineups = <
  T extends
    | useHierarchiedLineups_so5LineupImport
    | useHierarchiedSo5Lineups_so5Lineup,
>(
  so5LineupImport: T[]
) => {
  const hierarchiedLineups = (so5LineupImport || [])
    .filter(Boolean)
    .reduce<SortedLineups<T>>((acc, curr) => {
      const { so5Leaderboard } = curr;
      if (!so5Leaderboard) return acc;
      const existingLeaderboardGroup = acc.find(
        league =>
          league.so5LeaderboardGroup.slug ===
          so5Leaderboard.so5LeaderboardGroup.slug
      );

      if (existingLeaderboardGroup) {
        const existingCompetition = existingLeaderboardGroup.competitions.find(
          competition =>
            competition.mainRarityType === so5Leaderboard.mainRarityType &&
            competition.seasonality === so5Leaderboard.seasonality
        );

        if (existingCompetition) {
          existingCompetition.lineups.push(curr);
        } else {
          existingLeaderboardGroup.competitions.push({
            mainRarityType: so5Leaderboard.mainRarityType,
            seasonality: so5Leaderboard.seasonality,
            lineups: [curr],
          });
        }
      } else {
        acc.push({
          so5LeaderboardGroup: so5Leaderboard.so5LeaderboardGroup,
          competitions: [
            {
              mainRarityType: so5Leaderboard.mainRarityType,
              seasonality: so5Leaderboard.seasonality,
              lineups: [curr],
            },
          ],
        });
      }

      return acc;
    }, []);

  return hierarchiedLineups;
};

useHierarchiedLineups.fragments = {
  so5LineupImport: gql`
    fragment useHierarchiedLineups_so5LineupImport on So5LineupImport {
      id
      so5Leaderboard {
        slug
        seasonality
        mainRarityType
        iconUrl
        divisionIconUrl
        so5LeaderboardGroup {
          slug
        }
      }
    }
  ` as TypedDocumentNode<useHierarchiedLineups_so5LineupImport>,
  so5Lineup: gql`
    fragment useHierarchiedSo5Lineups_so5Lineup on So5Lineup {
      id
      so5Leaderboard {
        slug
        seasonality
        mainRarityType
        iconUrl
        divisionIconUrl
        so5LeaderboardGroup {
          slug
        }
      }
    }
  ` as TypedDocumentNode<useHierarchiedSo5Lineups_so5Lineup>,
};
